/**
 * P1-002: INTENT VERSIONING UI
 * ============================
 * Strategic Intent Registry and Version Control Interface
 * 
 * Vision: Track the evolution of strategic intent from V1 (Original) through approved changes.
 * This supports Intent Preservation, Governance Accountability, and Decision Integrity.
 * 
 * Features:
 * - Intent Version Registry (browse entities with intent)
 * - Version History View (V1 → V2 → V3...)
 * - Version Comparison Engine (visual diff)
 * - Intent Drift Analysis (current vs original)
 * - Decision Ledger Integration (auto-recorded)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  GitBranch, History, ArrowRight, ChevronRight, ChevronDown, Shield,
  FileText, DollarSign, Calendar, Target, AlertTriangle, CheckCircle,
  XCircle, Clock, User, Filter, Search, RefreshCw, Eye, Plus,
  TrendingUp, TrendingDown, Minus, Building, Briefcase, Folder,
  Lock, Unlock, Hash, BarChart3, Activity, Loader2, X, Copy, Check,
  GitCompare, Award
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

// ==========================================
// CONSTANTS
// ==========================================

const ENTITY_TYPES = {
  demand: { label: 'Demand', icon: Briefcase, color: 'blue' },
  project: { label: 'Project', icon: Folder, color: 'indigo' },
  program: { label: 'Program', icon: Building, color: 'purple' },
  portfolio: { label: 'Portfolio', icon: Building, color: 'amber' }
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', icon: Unlock, color: 'gray', bg: 'bg-gray-100 text-gray-700' },
  submitted: { label: 'Pending Approval', icon: Clock, color: 'amber', bg: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'green', bg: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'red', bg: 'bg-red-100 text-red-700' },
  superseded: { label: 'Superseded', icon: History, color: 'slate', bg: 'bg-slate-100 text-slate-600' }
};

const CHANGE_CATEGORIES = {
  initial: { label: 'Original Intent', color: 'indigo' },
  scope_change: { label: 'Scope Change', color: 'purple' },
  budget_change: { label: 'Budget Change', color: 'amber' },
  timeline_change: { label: 'Timeline Change', color: 'blue' },
  benefits_change: { label: 'Benefits Change', color: 'green' },
  combined: { label: 'Multiple Changes', color: 'red' }
};

const DRIFT_SEVERITY = {
  low: { color: 'green', bg: 'bg-green-100 text-green-700', label: 'Low' },
  medium: { color: 'amber', bg: 'bg-amber-100 text-amber-700', label: 'Medium' },
  high: { color: 'orange', bg: 'bg-orange-100 text-orange-700', label: 'High' },
  critical: { color: 'red', bg: 'bg-red-100 text-red-700', label: 'Critical' }
};

// ==========================================
// UTILITY COMPONENTS
// ==========================================

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const VersionBadge = ({ version, isBaseline }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
    isBaseline ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
  }`}>
    V{version}
    {isBaseline && <span className="text-indigo-200 ml-1">Baseline</span>}
  </span>
);

const DriftIndicator = ({ value, label }) => {
  const isPositive = value > 0;
  const isZero = value === 0;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">{label}:</span>
      <span className={`inline-flex items-center gap-1 font-medium ${
        isZero ? 'text-gray-500' : isPositive ? 'text-red-600' : 'text-green-600'
      }`}>
        {isZero ? (
          <Minus className="w-4 h-4" />
        ) : isPositive ? (
          <TrendingUp className="w-4 h-4" />
        ) : (
          <TrendingDown className="w-4 h-4" />
        )}
        {Math.abs(value)}%
      </span>
    </div>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600">
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

// ==========================================
// VERSION TIMELINE COMPONENT
// ==========================================

const VersionTimeline = ({ versions, currentVersionId, onSelectVersion }) => {
  if (!versions || versions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p>No intent versions found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="version-timeline">
      {versions.map((version, idx) => {
        const isSelected = version.id === currentVersionId;
        const statusConfig = STATUS_CONFIG[version.status] || STATUS_CONFIG.draft;
        const changeConfig = CHANGE_CATEGORIES[version.change_category] || CHANGE_CATEGORIES.initial;
        
        return (
          <div key={version.id} className="relative">
            {/* Timeline connector */}
            {idx < versions.length - 1 && (
              <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" />
            )}
            
            <button
              onClick={() => onSelectVersion(version)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                isSelected 
                  ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                  : 'border-transparent bg-white hover:border-gray-200 hover:shadow-sm'
              }`}
              data-testid={`version-item-${version.version}`}
            >
              <div className="flex items-start gap-3">
                {/* Version circle */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  version.is_baseline ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  V{version.version}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${changeConfig.color}-100 text-${changeConfig.color}-700`}>
                      {changeConfig.label}
                    </span>
                    <StatusBadge status={version.status} />
                    {version.status === 'approved' && (
                      <Lock className="w-3.5 h-3.5 text-green-500" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {version.change_reason}
                  </p>
                  
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {version.created_by_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(version.created_at).toLocaleDateString()}
                    </span>
                    {version.approved_at && (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        Approved {new Date(version.approved_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ==========================================
// VERSION DETAIL PANEL
// ==========================================

const VersionDetailPanel = ({ version, onClose, onCompare, allVersions }) => {
  const [integrityResult, setIntegrityResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [compareVersion, setCompareVersion] = useState(null);
  const [comparison, setComparison] = useState(null);

  const verifyIntegrity = async () => {
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/intent-versions/${version.id}/verify`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setIntegrityResult(data);
      toast[data.valid ? 'success' : 'error'](data.reason);
    } catch (err) {
      toast.error('Failed to verify integrity');
    } finally {
      setVerifying(false);
    }
  };

  const loadComparison = async (fromId) => {
    if (!fromId) {
      setComparison(null);
      setCompareVersion(null);
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE}/intent-versions/compare/${fromId}/${version.id}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setComparison(data.comparison);
      setCompareVersion(fromId);
    } catch (err) {
      toast.error('Failed to load comparison');
    }
  };

  if (!version) return null;

  return (
    <div className="h-full flex flex-col bg-white border-l" data-testid="version-detail-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-start justify-between z-10">
        <div>
          <div className="flex items-center gap-2">
            <VersionBadge version={version.version} isBaseline={version.is_baseline} />
            <StatusBadge status={version.status} />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mt-2">{version.entity_name}</h2>
          <p className="text-sm text-gray-500">{version.change_reason}</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Integrity Verification */}
        <div className={`rounded-xl p-4 border ${
          integrityResult 
            ? integrityResult.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {integrityResult ? (
                integrityResult.valid ? (
                  <Shield className="w-6 h-6 text-green-500" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                )
              ) : (
                <Shield className="w-6 h-6 text-gray-400" />
              )}
              <div>
                <p className="font-medium text-gray-900">Intent Integrity</p>
                <p className="text-sm text-gray-500">
                  {integrityResult ? integrityResult.reason : 'Verify this intent has not been tampered'}
                </p>
              </div>
            </div>
            <button
              onClick={verifyIntegrity}
              disabled={verifying}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              data-testid="verify-intent-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
            </button>
          </div>
          
          {integrityResult && (
            <div className="mt-3 pt-3 border-t text-xs font-mono grid grid-cols-2 gap-2">
              <div>
                <span className="text-gray-500">Stored:</span> {integrityResult.stored_hash}
              </div>
              <div>
                <span className="text-gray-500">Calculated:</span> {integrityResult.calculated_hash}
              </div>
            </div>
          )}
        </div>

        {/* Compare with previous versions */}
        {allVersions && allVersions.length > 1 && (
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-indigo-500" />
                Compare with Version
              </h3>
              <select
                value={compareVersion || ''}
                onChange={(e) => loadComparison(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm"
                data-testid="compare-version-select"
              >
                <option value="">Select version...</option>
                {allVersions.filter(v => v.id !== version.id).map(v => (
                  <option key={v.id} value={v.id}>
                    V{v.version} - {v.change_category?.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            
            {comparison && (
              <div className="space-y-3" data-testid="comparison-results">
                <div className="grid grid-cols-4 gap-3">
                  <div className={`p-3 rounded-lg ${comparison.summary.scope_changed ? 'bg-purple-50' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">Scope</p>
                    <p className={`font-semibold ${comparison.summary.scope_changed ? 'text-purple-700' : 'text-gray-400'}`}>
                      {comparison.summary.scope_changed ? 'Changed' : 'No Change'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${comparison.summary.budget_changed ? 'bg-amber-50' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">Budget</p>
                    <p className={`font-semibold ${comparison.summary.budget_changed ? 'text-amber-700' : 'text-gray-400'}`}>
                      {comparison.variances?.budget ? `${comparison.variances.budget > 0 ? '+' : ''}${comparison.variances.budget}%` : 'No Change'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${comparison.summary.timeline_changed ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">Timeline</p>
                    <p className={`font-semibold ${comparison.summary.timeline_changed ? 'text-blue-700' : 'text-gray-400'}`}>
                      {comparison.summary.timeline_changed ? 'Changed' : 'No Change'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${comparison.summary.benefits_changed ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">Benefits</p>
                    <p className={`font-semibold ${comparison.summary.benefits_changed ? 'text-green-700' : 'text-gray-400'}`}>
                      {comparison.variances?.benefits ? `${comparison.variances.benefits > 0 ? '+' : ''}${comparison.variances.benefits}%` : 'No Change'}
                    </p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600">
                  <strong>{comparison.summary.total_changes}</strong> changes detected between V{comparison.from_version} and V{comparison.to_version}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Scope */}
        {version.scope && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-500" />
              Scope
            </h3>
            <p className="text-sm text-gray-600 mb-3">{version.scope.description}</p>
            
            {version.scope.deliverables?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Deliverables</p>
                <div className="flex flex-wrap gap-2">
                  {version.scope.deliverables.map((d, i) => (
                    <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {version.scope.exclusions?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Exclusions</p>
                <div className="flex flex-wrap gap-2">
                  {version.scope.exclusions.map((e, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs line-through">
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Budget */}
        {version.budget && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-500" />
              Budget
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500">Total Cost</p>
                <p className="text-xl font-bold text-gray-900">
                  ${(version.budget.total_cost || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">CAPEX / OPEX</p>
                <p className="text-sm text-gray-700">
                  ${(version.budget.capex || 0).toLocaleString()} / ${(version.budget.opex || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Contingency</p>
                <p className="text-sm text-gray-700">
                  ${(version.budget.contingency || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {version.timeline && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              Timeline
            </h3>
            <div className="flex items-center gap-4 mb-3">
              <div>
                <p className="text-xs text-gray-500">Start</p>
                <p className="font-medium">{version.timeline.start_date}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">End</p>
                <p className="font-medium">{version.timeline.end_date}</p>
              </div>
              <div className="ml-auto">
                <p className="text-xs text-gray-500">Duration</p>
                <p className="font-medium">{version.timeline.duration_months} months</p>
              </div>
            </div>
            
            {version.timeline.key_milestones?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Milestones</p>
                <div className="space-y-1">
                  {version.timeline.key_milestones.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{m.name}</span>
                      <span className="text-gray-500">{m.target_date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Benefits */}
        {version.benefits && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              Benefits
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <p className="text-xs text-gray-500">Financial</p>
                <p className="text-xl font-bold text-green-600">
                  ${(version.benefits.financial_benefits || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ROI</p>
                <p className="text-lg font-semibold text-gray-900">
                  {version.benefits.roi_percentage}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Payback</p>
                <p className="text-lg font-semibold text-gray-900">
                  {version.benefits.payback_months} months
                </p>
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        {version.kpis?.length > 0 && (
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" />
              KPIs ({version.kpis.length})
            </h3>
            <div className="space-y-2">
              {version.kpis.map((kpi, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{kpi.name}</span>
                    <span className="text-sm text-indigo-600 font-semibold">{kpi.target_value}</span>
                  </div>
                  <p className="text-xs text-gray-500">{kpi.measurement_method} • {kpi.frequency}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Constraints & Assumptions */}
        <div className="grid grid-cols-2 gap-4">
          {version.constraints?.length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Constraints ({version.constraints.length})
              </h3>
              <div className="space-y-2">
                {version.constraints.map((c, i) => (
                  <div key={i} className="text-sm p-2 bg-red-50 rounded">
                    <p className="text-gray-700">{c.description}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      c.impact === 'high' ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'
                    }`}>
                      {c.impact} impact
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {version.assumptions?.length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-500" />
                Assumptions ({version.assumptions.length})
              </h3>
              <div className="space-y-2">
                {version.assumptions.map((a, i) => (
                  <div key={i} className="text-sm p-2 bg-amber-50 rounded">
                    <div className="flex items-start gap-2">
                      {a.validated ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500 mt-0.5" />
                      )}
                      <div>
                        <p className="text-gray-700">{a.description}</p>
                        <p className="text-xs text-gray-500">Risk: {a.risk_if_invalid}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Approval Info */}
        {version.approved_at && (
          <div className="bg-green-50 rounded-xl border border-green-200 p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div>
                <p className="font-medium text-green-800">Approved & Locked</p>
                <p className="text-sm text-green-600">
                  By {version.approved_by_name} on {new Date(version.approved_at).toLocaleString()}
                </p>
                {version.approval_notes && (
                  <p className="text-sm text-green-700 mt-1">{version.approval_notes}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rejection Info */}
        {version.rejected_at && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-medium text-red-800">Rejected</p>
                <p className="text-sm text-red-600">{version.rejection_reason}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// INTENT DRIFT CARD
// ==========================================

const IntentDriftCard = ({ entityType, entityId }) => {
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDrift = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/intent-versions/entity/${entityType}/${entityId}/drift`,
          { headers: getAuthHeaders() }
        );
        const data = await res.json();
        setDrift(data);
      } catch (err) {
        console.error('Failed to load drift', err);
      } finally {
        setLoading(false);
      }
    };
    loadDrift();
  }, [entityType, entityId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border p-5 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-24 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!drift?.has_baseline) {
    return (
      <div className="bg-gray-50 rounded-xl border border-dashed p-5 text-center text-gray-400">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No baseline intent defined</p>
      </div>
    );
  }

  const driftData = drift.drift;
  if (!driftData || driftData.overall_drift === 0) {
    return (
      <div className="bg-green-50 rounded-xl border border-green-200 p-5">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-green-500" />
          <div>
            <p className="font-semibold text-green-800">Intent Aligned</p>
            <p className="text-sm text-green-600">Current intent matches original baseline (V1)</p>
          </div>
        </div>
      </div>
    );
  }

  const severityConfig = DRIFT_SEVERITY[driftData.severity] || DRIFT_SEVERITY.low;

  return (
    <div className={`rounded-xl border p-5 ${
      driftData.severity === 'critical' ? 'bg-red-50 border-red-200' :
      driftData.severity === 'high' ? 'bg-orange-50 border-orange-200' :
      driftData.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
      'bg-green-50 border-green-200'
    }`} data-testid="intent-drift-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">Intent Drift (V1 → V{driftData.to_version})</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-3xl font-bold">{driftData.overall_drift}%</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${severityConfig.bg}`}>
              {severityConfig.label}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{driftData.recommendation}</p>
        </div>
        <Activity className={`w-8 h-8 text-${severityConfig.color}-500`} />
      </div>
      
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center p-2 bg-white/50 rounded">
          <p className="text-xs text-gray-500">Scope</p>
          <p className={`font-semibold ${driftData.scope_drift !== 0 ? 'text-purple-600' : 'text-gray-400'}`}>
            {driftData.scope_drift > 0 ? '+' : ''}{driftData.scope_drift}%
          </p>
        </div>
        <div className="text-center p-2 bg-white/50 rounded">
          <p className="text-xs text-gray-500">Budget</p>
          <p className={`font-semibold ${driftData.budget_drift !== 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            {driftData.budget_drift > 0 ? '+' : ''}{driftData.budget_drift}%
          </p>
        </div>
        <div className="text-center p-2 bg-white/50 rounded">
          <p className="text-xs text-gray-500">Timeline</p>
          <p className={`font-semibold ${driftData.timeline_drift !== 0 ? 'text-blue-600' : 'text-gray-400'}`}>
            {driftData.timeline_drift > 0 ? '+' : ''}{driftData.timeline_drift}%
          </p>
        </div>
        <div className="text-center p-2 bg-white/50 rounded">
          <p className="text-xs text-gray-500">Benefits</p>
          <p className={`font-semibold ${driftData.benefits_drift !== 0 ? 'text-green-600' : 'text-gray-400'}`}>
            {driftData.benefits_drift > 0 ? '+' : ''}{driftData.benefits_drift}%
          </p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const IntentVersioningPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  
  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState(searchParams.get('entity_type') || '');
  const [searchQuery, setSearchQuery] = useState('');

  // Load statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await fetch(`${API_BASE}/intent-versions/statistics`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error('Failed to load stats', err);
      }
    };
    loadStats();
  }, []);

  // Load entities with intent (from projects list)
  useEffect(() => {
    const loadEntities = async () => {
      setLoading(true);
      try {
        // Load projects as primary entities
        const projectsRes = await fetch(`${API_BASE}/projects`, {
          headers: getAuthHeaders()
        });
        const projectsData = await projectsRes.json();
        
        const projectEntities = (Array.isArray(projectsData) ? projectsData : projectsData.projects || [])
          .map(p => ({
            type: 'project',
            id: p.id || p._id,
            name: p.name,
            status: p.status,
            has_intent: false // Will be updated when checking intent
          }));
        
        setEntities(projectEntities);
      } catch (err) {
        console.error('Failed to load entities', err);
      } finally {
        setLoading(false);
      }
    };
    loadEntities();
  }, []);

  // Load versions for selected entity
  const loadVersions = useCallback(async (entityType, entityId) => {
    try {
      const res = await fetch(
        `${API_BASE}/intent-versions/entity/${entityType}/${entityId}?include_drafts=true`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setVersions(data.versions || []);
      if (data.versions?.length > 0) {
        setSelectedVersion(data.versions[0]); // Select most recent
      }
    } catch (err) {
      console.error('Failed to load versions', err);
      setVersions([]);
    }
  }, []);

  // Handle entity selection
  const handleSelectEntity = (entity) => {
    setSelectedEntity(entity);
    setSelectedVersion(null);
    loadVersions(entity.type, entity.id);
    setSearchParams({ entity_type: entity.type, entity_id: entity.id });
  };

  // Filter entities
  const filteredEntities = useMemo(() => {
    let result = entities;
    if (entityTypeFilter) {
      result = result.filter(e => e.type === entityTypeFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => e.name?.toLowerCase().includes(q));
    }
    return result;
  }, [entities, entityTypeFilter, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="intent-versioning-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <GitBranch className="w-7 h-7 text-purple-600" />
                Intent Versioning
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Track strategic intent evolution • V1 → V2 → V3... • Foundation for Decision Integrity
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Statistics Summary */}
              {stats && (
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{stats.total_intents}</p>
                    <p className="text-xs text-gray-500">Entities</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{stats.total_versions}</p>
                    <p className="text-xs text-gray-500">Versions</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-88px)]">
        {/* Left Panel - Entity List */}
        <div className="w-80 bg-white border-r flex flex-col">
          {/* Search & Filters */}
          <div className="p-4 border-b">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entities..."
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                data-testid="entity-search"
              />
            </div>
            <select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Entity Types</option>
              {Object.entries(ENTITY_TYPES).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          
          {/* Entity List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              </div>
            ) : filteredEntities.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No entities found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredEntities.map(entity => {
                  const typeConfig = ENTITY_TYPES[entity.type] || ENTITY_TYPES.project;
                  const Icon = typeConfig.icon;
                  const isSelected = selectedEntity?.id === entity.id;
                  
                  return (
                    <button
                      key={entity.id}
                      onClick={() => handleSelectEntity(entity)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                      }`}
                      data-testid={`entity-item-${entity.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${typeConfig.color}-100`}>
                          <Icon className={`w-4 h-4 text-${typeConfig.color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{entity.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{entity.type}</p>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 ${isSelected ? 'text-indigo-500' : ''}`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Middle Panel - Version Timeline */}
        <div className="flex-1 bg-gray-50 flex flex-col">
          {selectedEntity ? (
            <>
              {/* Entity Header */}
              <div className="bg-white border-b p-4">
                <h2 className="text-lg font-semibold text-gray-900">{selectedEntity.name}</h2>
                <p className="text-sm text-gray-500 capitalize">{selectedEntity.type}</p>
              </div>
              
              {/* Drift Card */}
              <div className="p-4 border-b bg-white">
                <IntentDriftCard entityType={selectedEntity.type} entityId={selectedEntity.id} />
              </div>
              
              {/* Version Timeline */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-700">Version History</h3>
                  <span className="text-sm text-gray-500">{versions.length} version{versions.length !== 1 ? 's' : ''}</span>
                </div>
                
                {versions.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-lg">No Intent Defined</p>
                    <p className="text-sm mt-1">Create V1 (Original Intent) to start tracking</p>
                  </div>
                ) : (
                  <VersionTimeline
                    versions={versions}
                    currentVersionId={selectedVersion?.id}
                    onSelectVersion={setSelectedVersion}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <GitBranch className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select an entity to view intent history</p>
                <p className="text-sm mt-1">Track scope, budget, timeline, and benefits evolution</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Version Detail */}
        {selectedVersion && (
          <div className="w-[480px]">
            <VersionDetailPanel
              version={selectedVersion}
              onClose={() => setSelectedVersion(null)}
              allVersions={versions}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default IntentVersioningPage;
